<script setup>
import GuideSetupTab from "../components/GuideSetupTab.vue";
import IntegrationPaddle from "../../../paddle/IntegrationPaddle.vue";
import UniversalCheckoutGuide from "../guides/UniversalCheckoutGuide.vue";


const props = defineProps({
    programId: {
        type: Number,
        required: true
    },
    config: {
        type: Object,
        default: null
    },
    programType: {
        type: String
    }
});

const paddleSlotTabs = [
    { slotName: 'checkout-open', tabTitle: 'Paddle.Checkout.open() method' },
    { slotName: 'html-attributes', tabTitle: 'HTML data attributes' },
    { slotName: 'checkout', tabTitle: 'Custom checkout' },
];

const checkoutExample = `<script>
    const partneroQueryParam = 'aff';
    const partneroCookieName = 'partnero_partner';

    function getCookie(name) {
        const cookieArr = document.cookie.split(";").map(cookie => cookie.trim());
        for (const cookie of cookieArr) {
            const [cookieName, cookieValue] = cookie.split("=");
            if (name === cookieName) {
                return decodeURIComponent(cookieValue);
            }
        }
        return null;
    }

    function injectCustomData() {
        const customerKey = new URLSearchParams(window.location.search).get(partneroQueryParam) || getCookie(partneroCookieName);

        if (customerKey) {
            const originalOpen = Paddle.Checkout.open;

            Paddle.Checkout.open = function(options) {
                options.customData = options.customData || {};
                options.customData.customer_key = customerKey;
                originalOpen.call(Paddle.Checkout, options);
            };
        }
    }

    document.addEventListener("DOMContentLoaded", injectCustomData);
</scr` + `ipt>`

const htmlExample = `<script>
    const partneroQueryParam = 'aff';
    const partneroCookieName = 'partnero_partner';

    function getCookie(name) {
        const cookieArr = document.cookie.split(";").map(cookie => cookie.trim());
        for (const cookie of cookieArr) {
            const [cookieName, cookieValue] = cookie.split("=");
            if (name === cookieName) {
                return decodeURIComponent(cookieValue);
            }
        }
        return null;
    }

    function updateCustomData() {
        const partnerCookieValue = getCookie(partneroCookieName);
        const referralKey = new URLSearchParams(window.location.search).get(partneroQueryParam);
        const customerKey = referralKey || partnerCookieValue;

        const signupButtons = document.querySelectorAll(".paddle_button");

        signupButtons.forEach(signupButton => {
            if (customerKey) {
                const customData = JSON.stringify({ customer_key: customerKey });
                signupButton.setAttribute("data-custom-data", customData);
            } else {
                signupButton.removeAttribute("data-custom-data");
            }
        });
    }

    document.addEventListener("DOMContentLoaded", updateCustomData);
</scr` + `ipt>`

</script>

<template>
    <guide-setup-tab :slot-tabs="paddleSlotTabs">
        <template #head>
            <div class="space-y-2">
                <h3 class="font-semibold text-md">
                    1. Connect your Paddle Billing account
                </h3>
                <div class="text-sm space-y-2">
                    <p>Link your Paddle Billing account to Partnero. This allows us to track sales and attribute them to referred customers accurately.</p>
                </div>
                <div v-if="config">
                    <integration-paddle
                        type="button"
                        :program-id="programId"
                        :integrations-config="config"
                    ></integration-paddle>
                </div>
            </div>

            <div class="space-y-2">
                <h3 class="font-semibold text-md">
                    2. Select your preferred Paddle Billing tracking method
                </h3>
                <div class="text-sm space-y-2">
                    <p>Choose the example that best matches your Paddle Billing integration to correctly identify purchases made by referred customers.</p>
                </div>
            </div>
        </template>

        <template #checkout-open>
            <div class="text-sm space-y-2">
                <p>Choose this method if you added Paddle Billing Overlay checkout directly to your website. This method works when checkout is triggered using Paddle's <a class="underline hover:no-underline" href="https://developer.paddle.com/build/checkout/build-overlay-checkout#paddle.checkout.open()-method" target="_blank">Paddle.Checkout.open() method</a> example. </p>
            </div>
            <div class="relative">
                <code-snippet :code="checkoutExample" language="javascript" />
                <small class="text-gray-500 mt-1 block">
                    Include the following script on every page containing payment links. It's important to place
                    this script at the bottom of the page, directly above the closing <badge type="neutral" title="</body"></badge>
                    tag for optimal functionality.
                </small>
            </div>
        </template>

        <template #html-attributes>
            <div class="text-sm space-y-2">
                <p>Choose this method if you added Paddle Billing Overlay checkout directly to your website. This method works when checkout is triggered using Paddle's <a class="underline hover:no-underline" href="https://developer.paddle.com/build/checkout/build-overlay-checkout#html-data-attributes" target="_blank">HTML data attributes</a> example. </p>
            </div>
            <div class="relative">
                <code-snippet :code="htmlExample" language="javascript" />
                <small class="text-gray-500 mt-1 block">
                    Include the following script on every page containing payment links. It's important to place
                    this script at the bottom of the page, directly above the closing <badge type="neutral" title="</body"></badge>
                    tag for optimal functionality.
                </small>
            </div>
        </template>

        <template #checkout>
            <universal-checkout-guide :program-type="programType" />
        </template>

    </guide-setup-tab>
</template>

<style scoped>

</style>
